import { useState, useMemo } from "react";
import { Popover } from "antd";
import { useNavigate } from "react-router-dom";
import styles from "./index.module.scss";
import Iconfont from "@utils/Iconfont";
import { IMAGE_TYPE, BATCH_IMAGES_TYPE, RESOURCE_TYPE } from "@utils/CONST";
import API from "@api/api";
import { workflowOfSendTask } from "@utils/workflowOfBuildTaskV2";

const imageUrlCache = new Map();
const imageUrlPendingCache = new Map();

const getImageCacheKey = (taskId, imageId) => `${taskId || ""}_${imageId || ""}`;

const fetchImageUrlWithCache = async (taskId, imageId) => {
    if (!taskId || !imageId) return "";
    const cacheKey = getImageCacheKey(taskId, imageId);

    if (imageUrlCache.has(cacheKey)) {
        return imageUrlCache.get(cacheKey) || "";
    }

    if (imageUrlPendingCache.has(cacheKey)) {
        return imageUrlPendingCache.get(cacheKey);
    }

    const pending = API.batchCommonDownload({ taskId, imageId })
        .then((res) => {
            if (res?.code != 0) throw new Error(res?.message);
            const url = res?.data || "";
            imageUrlCache.set(cacheKey, url);
            return url;
        })
        .finally(() => {
            imageUrlPendingCache.delete(cacheKey);
        });

    imageUrlPendingCache.set(cacheKey, pending);
    return pending;
}; 

const BottomBtnGroups = (props) => {
    const { item, className } = props;
    const navigate = useNavigate();
    const [imageUrl, setImageUrl] = useState();

    const taskId = useMemo(() => {
        return item?.taskId?.split("_")?.[0];
    }, [item]);

    const imageId = useMemo(() => {
        return item?.results?.[0]?.id;
    }, [item]);

    const ensureImageUrl = async () => {
        if (imageUrl) return imageUrl;
        try {
            const url = await fetchImageUrlWithCache(taskId, imageId);
            setImageUrl(url);
            return url;
        } catch (error) {
            console.error("Failed to fetch image:", error);
            return "";
        }
    };

    const handleClick = async (jumpTotype, targetType = "inputImage", extraState) => {
        const image = await ensureImageUrl();
        if (!image) return;
        const res = await workflowOfSendTask(jumpTotype, image, targetType, extraState);
        if (!res) return;
        const { pathname, options } = res;
        navigate(pathname, options);
    };

    const extractionClick = async () => {
        const url = await ensureImageUrl();
        window.open(url ? `/home/image_extraction?url=${encodeURIComponent(url)}` : `/home/image_extraction`, "_blank");
    };

    const resizeClick = async () => {
        const url = await ensureImageUrl();
        navigate(url ? `/home/aiEdit?menuKey=imageResize&url=${encodeURIComponent(url)}` : `/home/aiEdit?menuKey=imageResize`);
    };

    const jumpToAiEditWithImage = async (menuKey) => {
        const url = await ensureImageUrl();
        navigate("/home/aiEdit", { state: { menuKey, picUrl: url } })
    }

    const isAiEdit = useMemo(() => {
        if (!item) return false;
        return item.imageType == IMAGE_TYPE.AI_IMAGE_EXTRACTION ||
            item.imageType == IMAGE_TYPE.REMOVE_INPAINT ||
            item.imageType == IMAGE_TYPE.AI_BEAUTY ||
            item.imageType == IMAGE_TYPE.IMAGE_FINER ||
            item.imageType == IMAGE_TYPE.AI_SMART_BEAUTY ||
            item.imageType == IMAGE_TYPE.IMAGE_RESIZE ||
            item.imageType == IMAGE_TYPE.HAND_FIX;
    }, [item]);

    const MenuLink = ({ icon, label, onClick }) => (
        <p className={styles.footer_text} onClick={(e) => { e.stopPropagation(); onClick?.(); }}>
            <Iconfont type={icon} className={styles.Icon} />
            {label}
        </p>
    )

    const MenuSection = ({ title, menus = [] }) => {
        if (!menus.length) return null;
        return (
            <div className={styles.MenuSection}>
                {title && <div className={styles.GroupTitle}>{title}</div>}
                <div className={styles.GroupList}>
                    {menus.map((menu, i) => (
                        <MenuLink
                            key={`${menu.label}-${i}`}
                            icon={menu.icon}
                            label={menu.label}
                            onClick={menu.onClick}
                        />
                    ))}
                </div>
            </div>
        );
    };

    if (!item || isAiEdit || item?.orgData) return null;

    const modifyMenus = [];
    const videoMenus = [];
    const aiEditMenus = [];

    const showTryOnLikeActions =
        item.projectType === BATCH_IMAGES_TYPE.TRYON_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.IMAGE_FINER_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.CHANGE_MODEL_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.CHANGE_BKG_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.SIM_RECOLOR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.FUSIONWEAR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.GRASS_PLANT_BATCH;

    const showChangeModel =
        item.projectType === BATCH_IMAGES_TYPE.TRYON_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.IMAGE_FINER_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.CHANGE_BKG_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.SIM_RECOLOR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.FUSIONWEAR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.GRASS_PLANT_BATCH;

    if (showChangeModel) {
        modifyMenus.push({ label: "换模特", icon: "icon-aiModelOutlined", onClick: () => handleClick(IMAGE_TYPE.MODEL) });
    }

    const showChangeBackground =
        item.projectType === BATCH_IMAGES_TYPE.TRYON_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.IMAGE_FINER_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.SIM_RECOLOR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.FUSIONWEAR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.GRASS_PLANT_BATCH;

    if (showChangeBackground) {
        modifyMenus.push({ label: "换背景", icon: "icon-aibgOutlined", onClick: () => handleClick(IMAGE_TYPE.BACKGROUND) });
    }

    if (showTryOnLikeActions) {
        modifyMenus.push({ label: "换姿势", icon: "icon-poseFantasyOutlined", onClick: () => handleClick(IMAGE_TYPE.POSE_TRYON) });
    }

    const showChangeClothes =
        item.projectType === BATCH_IMAGES_TYPE.IMAGE_FINER_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.CHANGE_MODEL_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.CHANGE_BKG_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.SIM_RECOLOR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.FUSIONWEAR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.GRASS_PLANT_BATCH;

    if (showChangeClothes) {
        modifyMenus.push({
            label: (item.projectType === BATCH_IMAGES_TYPE.FUSIONWEAR_BATCH || item.projectType === BATCH_IMAGES_TYPE.GRASS_PLANT_BATCH) ? "换衣服" : "AI试衣",
            icon: "icon-outfitSmallOutlined",
            onClick: () => handleClick(IMAGE_TYPE.CLOTHES, RESOURCE_TYPE.TRYON_MODEL),
        });
    }

    if (showTryOnLikeActions) {
        modifyMenus.push({ label: "换鞋子", icon: "icon-shoesOutlined", onClick: () => handleClick(IMAGE_TYPE.SHOES, RESOURCE_TYPE.TRYON_SHOES) });
        modifyMenus.push({ label: "换眼镜", icon: "icon-glassesOutlined", onClick: () => handleClick(IMAGE_TYPE.GLASSES_TRYON, RESOURCE_TYPE.GLASSES_TRYON) });
        modifyMenus.push({ label: "换项链", icon: "icon-necklaceOutlined", onClick: () => handleClick(IMAGE_TYPE.NECKLACE_TRYON, RESOURCE_TYPE.NECKLACE_TRYON, { silentSelect: true }) });
        modifyMenus.push({ label: "同版型试衣", icon: "icon-sametryOutlined", onClick: () => handleClick(IMAGE_TYPE.SIM_TRYON, RESOURCE_TYPE.SIM_TRYON) });
    }

    if (item.projectType !== BATCH_IMAGES_TYPE.SIM_RECOLOR_BATCH) {
        modifyMenus.push({ label: "服饰换色", icon: "icon-recolorOutlined", onClick: () => handleClick(IMAGE_TYPE.CLOTH_RECOLOR) });
    }

    const showCreativeImageGen =
        item.imageType !== IMAGE_TYPE.MODEL_VIDEO &&
        item.imageType !== IMAGE_TYPE.IMAGE_TO_VIDEO &&
        item.projectType !== BATCH_IMAGES_TYPE.IMAGE_TO_VIDEO_BATCH;

    if (showCreativeImageGen) {
        modifyMenus.unshift({ label: "AI创意生图", icon: "icon-genimageOutlined", onClick: () => handleClick(IMAGE_TYPE.CREATIVE_IMAGE_GEN) });
    }

    const showFusionWear =
        item.projectType !== BATCH_IMAGES_TYPE.WATERMARK_REMOVE_BATCH &&
        item.projectType !== BATCH_IMAGES_TYPE.FUSIONWEAR_BATCH;

    if (showFusionWear) {
        modifyMenus.push({ label: "换穿搭", icon: "icon-outfitSmallOutlined", onClick: () => handleClick(IMAGE_TYPE.FUSION_WEAR, RESOURCE_TYPE.FUSIONWEAR_MODEL) });
    }

    if (item.projectType !== BATCH_IMAGES_TYPE.WATERMARK_REMOVE_BATCH &&
        item.projectType !== BATCH_IMAGES_TYPE.GRASS_PLANT_BATCH) {
        modifyMenus.push({ label: "去种草", icon: "icon-sProductshowcaseOutlined", onClick: () => handleClick(IMAGE_TYPE.GRASS_PLANT) });
    }

    if (item.projectType === BATCH_IMAGES_TYPE.WATERMARK_REMOVE_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.IMAGE_FINER_BATCH) {
        modifyMenus.push({ label: "商品换背", icon: "icon-commidityOutlined", onClick: () => handleClick(IMAGE_TYPE.COMMODITY) });
    }

    const showCommodityRecolor =
        item.projectType === BATCH_IMAGES_TYPE.TRYON_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.IMAGE_FINER_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.CHANGE_MODEL_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.CHANGE_BKG_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.SIM_RECOLOR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.WATERMARK_REMOVE_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.FUSIONWEAR_BATCH ||
        item.projectType === BATCH_IMAGES_TYPE.GRASS_PLANT_BATCH;

    if (showCommodityRecolor) {
        modifyMenus.push({ label: "商品换色", icon: "icon-recolorOutlined", onClick: () => handleClick(IMAGE_TYPE.COMMODITYRECOLOR) });
    }

    videoMenus.push({ label: "图生视频", icon: "icon-tryonVideoOutlined", onClick: () => handleClick(IMAGE_TYPE.IMAGE_TO_VIDEO) });

    if (showTryOnLikeActions) {
        videoMenus.push({ label: "动作模仿", icon: "icon-tryonVideoOutlined", onClick: () => handleClick(IMAGE_TYPE.MODEL_VIDEO) });
    }

    aiEditMenus.push(
        { label: "抠图", icon: "icon-mattingOutlined", onClick: extractionClick },
        { label: "改尺寸", icon: "icon-picResizeOutlined", onClick: resizeClick },
        { label: "AI超清", icon: "icon-hasHDOutlined", onClick: () => jumpToAiEditWithImage("imageFiner") },
        { label: "修光影", icon: "icon-lightIconOutlined", onClick: () => jumpToAiEditWithImage("filllight") },
        { label: "修手", icon: "icon-handRepairOutlined", onClick: () => jumpToAiEditWithImage("handRepair") },
        { label: "去杂物", icon: "icon-magicRemoveOutlined", onClick: () => jumpToAiEditWithImage("magicErase") },
    );

    if (item.projectType != BATCH_IMAGES_TYPE.WATERMARK_REMOVE_BATCH) {
        aiEditMenus.push({ label: "去水印", icon: "icon-removeWatermark", onClick: () => handleClick(IMAGE_TYPE.WATERMARK_REMOVE) });
    }

    const primaryMenus = modifyMenus.filter((menu) => menu.label === "AI创意生图");
    const modifyGroupMenus = modifyMenus.filter((menu) => menu.label !== "AI创意生图");
    const modifySplitIndex = Math.ceil(modifyGroupMenus.length / 2);
    const modifyGroupMenusLeft = modifyGroupMenus.slice(0, modifySplitIndex);
    const modifyGroupMenusRight = modifyGroupMenus.slice(modifySplitIndex);

    const popover = (
        <div className={styles.popoverContent} onClick={(e) => e.stopPropagation()}>
            {(primaryMenus.length > 0 || modifyGroupMenusLeft.length > 0) && (
                <div className={styles.Column}>
                    <MenuSection menus={primaryMenus} />
                    <MenuSection title={modifyGroupMenusLeft.length > 0 ? "修改图片" : ""} menus={modifyGroupMenusLeft} />
                </div>
            )}
            {(modifyGroupMenusRight.length > 0 || videoMenus.length > 0) && (
                <div className={styles.Column}>
                    <MenuSection menus={modifyGroupMenusRight} />
                    <MenuSection title="视频生成" menus={videoMenus} />
                </div>
            )}
            {aiEditMenus.length > 0 && (
                <div className={styles.Column}>
                    <MenuSection title="AI编辑" menus={aiEditMenus} />
                </div>
            )}
        </div>
    );

    return (
        <div className={`${styles.BottomBtnGroups} ${className || ""}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.MoreOutMenuContent}>
                <Popover placement="top" content={popover} overlayClassName={styles.BottomMenuPopover}>
                    <span className={`${styles.spanItem} ${styles.spanItem2}`}>
                        <Iconfont type="icon-menuOutlined" />
                    </span>
                </Popover>
            </div>
        </div>
    );
};

export default BottomBtnGroups;
